<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { dailyReportsApi, type DailyReport } from '@/api/entities/review/dailyReports'

definePage({
  meta: {
    title: '일일 리포트',
    action: 'read',
    subject: 'DailyReport',
  },
})

const { snackbar, handleError } = useErrorHandler()

const loading = ref(false)
const reports = ref<DailyReport[]>([])
const page = ref(1)
const lastPage = ref(1)
const total = ref(0)

const startDate = ref('')
const endDate = ref('')

// Generate dialog
const generateDialog = ref(false)
const generateDate = ref(new Date().toISOString().slice(0, 10))
const generateForce = ref(false)
const generating = ref(false)

// Detail dialog
const detailDialog = ref(false)
const selectedReport = ref<DailyReport | null>(null)
const loadingDetail = ref(false)

// Analytics

const headers = [
  { title: '날짜', key: 'report_date', width: '120px' },
  { title: '총 리뷰', key: 'total_reviews', align: 'end' as const },
  { title: '이관 성공', key: 'transferred_reviews', align: 'end' as const },
  { title: '실패', key: 'failed_reviews', align: 'end' as const },
  { title: '성공률', key: 'success_rate', align: 'end' as const },
  { title: '활성 클라이언트', key: 'active_clients', align: 'end' as const },
  { title: '', key: 'actions', sortable: false, width: '150px' },
]

const formatDate = (dateStr: string) => {
  return dateStr?.slice(0, 10) ?? ''
}

const loadReports = async () => {
  loading.value = true
  try {
    const params: Record<string, unknown> = { page: page.value, per_page: 15 }
    if (startDate.value) params.start_date = startDate.value
    if (endDate.value) params.end_date = endDate.value

    const { success, data, error } = await dailyReportsApi.list(params as Parameters<typeof dailyReportsApi.list>[0])
    if (success && data) {
      reports.value = data.data
      lastPage.value = data.last_page
      total.value = data.total
    }
    else {
      handleError(error)
    }
  }
  catch (e) {
    handleError(e)
  }
  finally {
    loading.value = false
  }
}

const showDetail = async (report: DailyReport) => {
  detailDialog.value = true
  loadingDetail.value = true
  try {
    const { success, data, error } = await dailyReportsApi.show(report.id)
    if (success && data) {
      selectedReport.value = data
    }
    else {
      handleError(error)
    }
  }
  catch (e) {
    handleError(e)
  }
  finally {
    loadingDetail.value = false
  }
}

const handleGenerate = async () => {
  if (generating.value) return
  generating.value = true
  try {
    const { success, data, error, status } = await dailyReportsApi.generate(generateDate.value, generateForce.value)
    if (success && data?.id) {
      snackbar.success('리포트가 생성되었습니다.')
      generateDialog.value = false
      generateForce.value = false
      await loadReports()
    }
    else if (status === 409) {
      snackbar.warning('해당 날짜의 리포트가 이미 존재합니다. 강제 재생성을 체크하세요.')
    }
    else if (success) {
      snackbar.info(data?.message ?? '이관 건수가 없어 리포트가 생성되지 않았습니다.')
    }
    else {
      handleError(error)
    }
  }
  finally {
    generating.value = false
  }
}

const downloadReport = (id: number) => {
  window.open(dailyReportsApi.downloadUrl(id), '_blank')
}

const sendNotification = async (id: number) => {
  const { success, data, error, status } = await dailyReportsApi.sendNotification(id)
  if (success) {
    snackbar.success(data?.message ?? '알림 발송 요청 완료')
  }
  else if (status === 501) {
    snackbar.warning('알림톡 발송 기능은 아직 구현되지 않았습니다.')
  }
  else {
    handleError(error)
  }
}

const onPageChange = (p: number) => {
  page.value = p
  loadReports()
}

const applyFilter = () => {
  page.value = 1
  loadReports()
}

const clearFilter = () => {
  startDate.value = ''
  endDate.value = ''
  page.value = 1
  loadReports()
}

onMounted(() => {
  loadReports()
})
</script>

<template>
  <VCard>
    <VCardTitle class="d-flex align-center justify-space-between">
      <span>일일 리포트</span>
      <VBtn
        color="primary"
        prepend-icon="ri-add-line"
        @click="generateDialog = true"
      >
        리포트 생성
      </VBtn>
    </VCardTitle>

    <VCardText>
      <!-- Filters -->
      <VRow class="mb-4">
        <VCol
          cols="12"
          md="3"
        >
          <VTextField
            v-model="startDate"
            label="시작일"
            type="date"
            density="compact"
            hide-details
          />
        </VCol>
        <VCol
          cols="12"
          md="3"
        >
          <VTextField
            v-model="endDate"
            label="종료일"
            type="date"
            density="compact"
            hide-details
          />
        </VCol>
        <VCol
          cols="12"
          md="3"
          class="d-flex ga-2"
        >
          <VBtn
            color="primary"
            variant="outlined"
            @click="applyFilter"
          >
            검색
          </VBtn>
          <VBtn
            variant="text"
            @click="clearFilter"
          >
            초기화
          </VBtn>
        </VCol>
      </VRow>

      <!-- Table -->
      <VDataTableServer
        :headers="headers"
        :items="reports"
        :items-length="total"
        :loading="loading"
        :page="page"
        :items-per-page="15"
        @update:page="onPageChange"
      >
        <template #item.report_date="{ item }">
          {{ formatDate(item.report_date) }}
        </template>

        <template #item.success_rate="{ item }">
          <VChip
            :color="item.success_rate >= 95 ? 'success' : item.success_rate >= 80 ? 'warning' : 'error'"
            size="small"
          >
            {{ item.success_rate }}%
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            icon
            size="small"
            variant="text"
            @click="showDetail(item)"
          >
            <VIcon icon="ri-eye-line" />
            <VTooltip
              activator="parent"
              location="top"
            >
              상세 보기
            </VTooltip>
          </VBtn>
          <VBtn
            icon
            size="small"
            variant="text"
            @click="downloadReport(item.id)"
          >
            <VIcon icon="ri-download-line" />
            <VTooltip
              activator="parent"
              location="top"
            >
              CSV 다운로드
            </VTooltip>
          </VBtn>
          <VBtn
            icon
            size="small"
            variant="text"
            @click="sendNotification(item.id)"
          >
            <VIcon icon="ri-notification-line" />
            <VTooltip
              activator="parent"
              location="top"
            >
              알림 발송
            </VTooltip>
          </VBtn>
        </template>
      </VDataTableServer>
    </VCardText>
  </VCard>

  <!-- Generate Dialog -->
  <VDialog
    v-model="generateDialog"
    max-width="400"
  >
    <VCard>
      <VCardTitle>리포트 생성</VCardTitle>
      <VCardText>
        <VTextField
          v-model="generateDate"
          label="날짜"
          type="date"
          class="mb-4"
        />
        <VCheckbox
          v-model="generateForce"
          label="기존 리포트 강제 재생성"
        />
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn
          variant="text"
          @click="generateDialog = false"
        >
          취소
        </VBtn>
        <VBtn
          color="primary"
          :loading="generating"
          :disabled="generating"
          @click="handleGenerate"
        >
          생성
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Detail Dialog -->
  <VDialog
    v-model="detailDialog"
    max-width="800"
  >
    <VCard>
      <VCardTitle>
        리포트 상세 — {{ selectedReport ? formatDate(selectedReport.report_date) : '' }}
      </VCardTitle>
      <VCardText>
        <VProgressLinear
          v-if="loadingDetail"
          indeterminate
        />
        <template v-else-if="selectedReport">
          <!-- Summary -->
          <VRow class="mb-4">
            <VCol
              cols="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis">
                총 리뷰
              </div>
              <div class="text-h6">
                {{ selectedReport.total_reviews }}
              </div>
            </VCol>
            <VCol
              cols="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis">
                이관 성공
              </div>
              <div class="text-h6 text-success">
                {{ selectedReport.transferred_reviews }}
              </div>
            </VCol>
            <VCol
              cols="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis">
                실패
              </div>
              <div class="text-h6 text-error">
                {{ selectedReport.failed_reviews }}
              </div>
            </VCol>
            <VCol
              cols="6"
              md="3"
            >
              <div class="text-caption text-medium-emphasis">
                성공률
              </div>
              <div class="text-h6">
                {{ selectedReport.success_rate }}%
              </div>
            </VCol>
          </VRow>

          <!-- Client Statistics -->
          <h4 class="mb-2">
            클라이언트별 통계
          </h4>
          <VTable
            v-if="selectedReport.client_statistics?.length"
            density="compact"
          >
            <thead>
              <tr>
                <th>클라이언트</th>
                <th class="text-end">
                  총 리뷰
                </th>
                <th class="text-end">
                  이관
                </th>
                <th class="text-end">
                  실패
                </th>
                <th class="text-end">
                  성공률
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="stat in selectedReport.client_statistics"
                :key="stat.id"
              >
                <td>{{ stat.client_name }}</td>
                <td class="text-end">
                  {{ stat.total_reviews }}
                </td>
                <td class="text-end">
                  {{ stat.transferred_reviews }}
                </td>
                <td class="text-end">
                  {{ stat.failed_reviews }}
                </td>
                <td class="text-end">
                  {{ stat.success_rate }}%
                </td>
              </tr>
            </tbody>
          </VTable>
          <div
            v-else
            class="text-medium-emphasis"
          >
            클라이언트 통계 없음
          </div>

          <!-- Driver Statistics -->
          <h4 class="mt-4 mb-2">
            드라이버별 통계
          </h4>
          <VTable
            v-if="selectedReport.driver_statistics?.length"
            density="compact"
          >
            <thead>
              <tr>
                <th>드라이버</th>
                <th class="text-end">
                  총 리뷰
                </th>
                <th class="text-end">
                  이관
                </th>
                <th class="text-end">
                  실패
                </th>
                <th class="text-end">
                  성공률
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="stat in selectedReport.driver_statistics"
                :key="stat.id"
              >
                <td>{{ stat.driver_code }}</td>
                <td class="text-end">
                  {{ stat.total_reviews }}
                </td>
                <td class="text-end">
                  {{ stat.transferred_reviews }}
                </td>
                <td class="text-end">
                  {{ stat.failed_reviews }}
                </td>
                <td class="text-end">
                  {{ stat.success_rate }}%
                </td>
              </tr>
            </tbody>
          </VTable>
          <div
            v-else
            class="text-medium-emphasis"
          >
            드라이버 통계 없음
          </div>
        </template>
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn
          v-if="selectedReport"
          variant="outlined"
          prepend-icon="ri-download-line"
          @click="downloadReport(selectedReport.id)"
        >
          CSV 다운로드
        </VBtn>
        <VBtn
          variant="text"
          @click="detailDialog = false"
        >
          닫기
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
